import { ChargementImpossible } from '@/components/ChargementImpossible'
import { useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { useUIStore } from '@/stores/useUIStore'
import {
  usePowerDialerMyCampaigns,
  type PowerDialerMyCampaign,
} from '@/api/powerDialer'
import { PowerDialerSessionTab } from './PowerDialerSessionTab'

const STATUS_COLORS: Record<string, { bg: string; darkBg: string }> = {
  active: {
    bg: 'bg-green-100 text-green-700',
    darkBg: 'bg-green-500/10 text-green-400',
  },
  paused: {
    bg: 'bg-amber-100 text-amber-700',
    darkBg: 'bg-amber-500/10 text-amber-400',
  },
  ready: {
    bg: 'bg-blue-100 text-blue-700',
    darkBg: 'bg-blue-500/10 text-blue-400',
  },
  completed: {
    bg: 'bg-slate-100 text-slate-600',
    darkBg: 'bg-slate-500/10 text-slate-400',
  },
}

export function PowerDialerAgentTab() {
  const { t } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const { data: campaigns = [], isLoading, isError } = usePowerDialerMyCampaigns()

  // Session active stockée dans l'URL (?session=42) pour permettre F5,
  // bookmark, partage de lien et ouverture dans un nouvel onglet.
  const [searchParams, setSearchParams] = useSearchParams()
  const activeCampaignId =
    Number(searchParams.get('session') || '') || null

  const setActiveCampaignId = (id: number | null) => {
    const next = new URLSearchParams(searchParams)
    if (id) next.set('session', String(id))
    else next.delete('session')
    setSearchParams(next, { replace: true })
  }

  if (activeCampaignId) {
    return (
      <PowerDialerSessionTab
        campaignId={activeCampaignId}
        onBack={() => setActiveCampaignId(null)}
      />
    )
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20 h-full">
        <div
          className={cn(
            'animate-spin rounded-full h-8 w-8 border-b-2',
            darkMode ? 'border-white' : 'border-slate-900',
          )}
        />
      </div>
    )
  }

  if (isError && campaigns.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <ChargementImpossible darkMode={darkMode} />
      </div>
    )
  }

  if (campaigns.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="text-center py-20">
          <div
            className={cn(
              'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4',
              darkMode ? 'bg-slate-800' : 'bg-slate-100',
            )}
          >
            <svg
              className={cn(
                'w-8 h-8',
                darkMode ? 'text-slate-600' : 'text-slate-300',
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5"
              />
            </svg>
          </div>
          <h3
            className={cn(
              'text-lg font-semibold mb-2',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {t('powerDialer.agent.noCampaignTitle')}
          </h3>
          <p
            className={cn(
              'text-sm',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {t('powerDialer.agent.noCampaignHint')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 h-full">
      <div className="flex items-center gap-2 mb-4">
        <h2
          className={cn(
            'text-xl font-bold tracking-tight',
            darkMode ? 'text-white' : 'text-slate-900',
          )}
        >
          {t('manager.powerDialer.sectionTitle')}
        </h2>
      </div>

      <div
        className={cn(
          'rounded-xl border overflow-hidden',
          darkMode ? 'border-slate-700' : 'border-slate-200',
        )}
      >
        <table className="w-full">
          <thead>
            <tr
              className={cn(
                'border-b text-xs font-semibold uppercase tracking-wider',
                darkMode
                  ? 'bg-slate-800/60 border-slate-700 text-slate-400'
                  : 'bg-slate-50 border-slate-200 text-slate-500',
              )}
            >
              <th className="px-5 py-3 text-left">
                {t('manager.powerDialer.list.table.campaign')}
              </th>
              <th className="px-5 py-3 text-left">
                {t('manager.powerDialer.list.table.status')}
              </th>
              <th className="px-5 py-3 text-left">
                {t('powerDialer.agent.prospects')}
              </th>
              <th className="px-5 py-3 text-left">
                {t('powerDialer.agent.called')}
              </th>
              <th className="px-5 py-3 text-left">
                {t('manager.powerDialer.list.table.interested')}
              </th>
              <th className="px-5 py-3 text-left">
                {t('manager.powerDialer.list.table.progress')}
              </th>
              <th className="px-5 py-3" />
            </tr>
          </thead>
          <tbody
            className={cn(
              'divide-y',
              darkMode ? 'divide-slate-700/50' : 'divide-slate-100',
            )}
          >
            {campaigns.map((campaign: PowerDialerMyCampaign) => {
              const statusKey = STATUS_COLORS[campaign.status]
                ? campaign.status
                : 'ready'
              const statusCfg = STATUS_COLORS[statusKey]
              const progress =
                campaign.total_prospects > 0
                  ? Math.round(
                      (campaign.prospects_called / campaign.total_prospects) * 100,
                    )
                  : 0

              return (
                <tr
                  key={campaign.id}
                  onClick={() => setActiveCampaignId(campaign.id)}
                  className={cn(
                    'cursor-pointer transition-colors',
                    darkMode ? 'hover:bg-slate-800/50' : 'hover:bg-slate-50/80',
                  )}
                >
                  <td className="px-5 py-4">
                    <span
                      className={cn(
                        'font-medium text-sm',
                        darkMode ? 'text-white' : 'text-slate-900',
                      )}
                    >
                      {campaign.name}
                    </span>
                    {campaign.description && (
                      <p
                        className={cn(
                          'text-xs mt-0.5',
                          darkMode ? 'text-slate-400' : 'text-slate-500',
                        )}
                      >
                        {campaign.description}
                      </p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span
                      className={cn(
                        'px-2 py-0.5 rounded-full text-xs font-medium',
                        darkMode ? statusCfg.darkBg : statusCfg.bg,
                      )}
                    >
                      {t(`manager.powerDialer.list.status.${statusKey}`)}
                    </span>
                  </td>
                  <td
                    className={cn(
                      'px-5 py-4 text-sm',
                      darkMode ? 'text-slate-300' : 'text-slate-700',
                    )}
                  >
                    {campaign.total_prospects}
                  </td>
                  <td
                    className={cn(
                      'px-5 py-4 text-sm',
                      darkMode ? 'text-slate-300' : 'text-slate-700',
                    )}
                  >
                    {campaign.prospects_called}
                  </td>
                  <td
                    className={cn(
                      'px-5 py-4 text-sm font-medium',
                      darkMode ? 'text-green-400' : 'text-green-600',
                    )}
                  >
                    {campaign.prospects_interested}
                  </td>
                  <td className="px-5 py-4 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <div
                        className={cn(
                          'flex-1 h-1.5 rounded-full overflow-hidden',
                          darkMode ? 'bg-slate-700' : 'bg-slate-200',
                        )}
                      >
                        <div
                          className="h-full bg-blue-600 rounded-full transition-all"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <span
                        className={cn(
                          'text-xs tabular-nums',
                          darkMode ? 'text-slate-400' : 'text-slate-500',
                        )}
                      >
                        {progress}%
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <span
                      className={cn(
                        'text-xs font-medium',
                        darkMode ? 'text-blue-400' : 'text-blue-600',
                      )}
                    >
                      {t('powerDialer.agent.open')}
                    </span>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
